import React, { useCallback, useEffect, useMemo, useState } from 'react';
import WellMapV2 from '../assets/WellMapV2.jsx';
import { fetchWorkflow } from '../assets/api';
import { absApi } from './api';
import {
  balanceSeries,
  downsideFromPreset,
  fmtMM,
  fmtPct,
  fmtX,
  linkRunBody,
  loadPool,
  savePool,
  triggerSegments,
} from './absLink.js';
import '../assets/assetWorkflow.css';

const SEG_COLORS = { normal: '#059669', sweep: '#d97706', default: '#dc2626', repaid: '#94a3b8' };
const LINE_COLORS = ['#163a4f', '#14b8a6', '#d97706', '#7c3aed'];

function MiniLines({ series, height = 160, testid }) {
  const w = 640;
  const all = series.flatMap((s) => s.points);
  const xMax = Math.max(1, ...all.map((p) => p[0]));
  const yMax = Math.max(1e-9, ...all.map((p) => p[1]));
  const path = (pts) => pts.map((p, i) => `${i ? 'L' : 'M'}${((p[0] / xMax) * (w - 40) + 36).toFixed(1)},${(height - 20 - (p[1] / yMax) * (height - 30)).toFixed(1)}`).join('');
  return (
    <svg viewBox={`0 0 ${w} ${height}`} width="100%" height={height} data-testid={testid} role="img">
      <line x1="36" y1={height - 20} x2={w - 4} y2={height - 20} stroke="#cbd5e1" />
      <text x="2" y="14" fontSize="10" fill="#64748b">{yMax.toFixed(1)}</text>
      <text x="2" y={height - 22} fontSize="10" fill="#64748b">0</text>
      {series.map((s, i) => (
        <g key={s.id}>
          <path d={path(s.points)} fill="none" stroke={LINE_COLORS[i % LINE_COLORS.length]} strokeWidth="2" />
          <text x={w - 120} y={14 + i * 12} fontSize="10" fill={LINE_COLORS[i % LINE_COLORS.length]}>{s.id}</text>
        </g>
      ))}
    </svg>
  );
}

function TriggerTimeline({ rows, testid }) {
  const segs = triggerSegments(rows);
  const total = segs.reduce((a, s) => a + s.months, 0) || 1;
  return (
    <div data-testid={testid}>
      <div style={{ display: 'flex', height: 18, borderRadius: 4, overflow: 'hidden' }}>
        {segs.map((s, i) => (
          <div key={i} title={`${s.state} ${s.from}→${s.to} ${s.breaches.join(', ')}`} style={{ width: `${(s.months / total) * 100}%`, background: SEG_COLORS[s.state] }} />
        ))}
      </div>
      <div className="aw-small">
        {segs.filter((s) => s.state !== 'normal' && s.state !== 'repaid').slice(0, 6).map((s, i) => (
          <span key={i} style={{ marginRight: 12 }}>
            <b style={{ color: SEG_COLORS[s.state] }}>{s.state}</b> {s.from}→{s.to} ({s.months} mo{s.breaches.length ? `: ${s.breaches.join(', ')}` : ''})
          </span>
        ))}
        {segs.every((s) => s.state === 'normal' || s.state === 'repaid') && <span>No trigger breaches.</span>}
      </div>
    </div>
  );
}

function SummaryKpis({ s, label, testid }) {
  if (!s) return null;
  return (
    <div className="abs-link-kpis" data-testid={testid}>
      <h4>{label}</h4>
      <dl className="abs-dl">
        <div><dt>Notes at close</dt><dd>{fmtMM(s.notesAtCloseMM)}</dd></div>
        <div><dt>Collateral PV-10 (SPV + hedge MTM)</dt><dd>{fmtMM(s.collateralPv10MM)}</dd></div>
        <div><dt>Advance rate / closing LTV</dt><dd>{fmtPct(s.advanceRate)}</dd></div>
        <div><dt>Min DSCR</dt><dd>{fmtX(s.minDscr)}</dd></div>
        <div><dt>Max LTV</dt><dd>{fmtPct(s.maxLtv)}</dd></div>
        <div><dt>Sweep months</dt><dd>{s.sweepMonths}{s.firstSweepMonth ? ` (from ${s.firstSweepMonth})` : ''}</dd></div>
        <div><dt>Event of default</dt><dd>{s.eventOfDefault ? `yes (${s.firstDefaultMonth})` : 'no'}</dd></div>
        <div><dt>Payoff / legal final</dt><dd>{s.payoffMonth || 'not repaid'} · {s.legalFinalMet ? 'met' : 'MISSED'} ({Math.round((s.legalFinalMonths || 0) / 12)} yr)</dd></div>
        <div><dt>WAL</dt><dd>{Object.entries(s.walYears || {}).map(([k, v]) => `${k} ${v ?? '—'}y`).join(' · ')}</dd></div>
        <div><dt>Cash to parent</dt><dd>{fmtMM(s.toParentMM)}</dd></div>
      </dl>
    </div>
  );
}

function PerUnit({ model, pdf }) {
  const pdfBy = Object.fromEntries((pdf || []).map((r) => [r.key, r.perUnit]));
  return (
    <div className="abs-table-wrap">
      <table className="abs-table" data-testid="abs-link-per-unit">
        <thead><tr><th>$/Mcfe (year 1)</th><th>Deal</th><th>PDF illustration</th></tr></thead>
        <tbody>
          {(model?.rows || []).map((r) => (
            <tr key={r.key} className={['sncf', 'hedgedRevenue', 'toParent'].includes(r.key) ? 'abs-row-strong' : ''}>
              <td>{r.label}</td>
              <td className="aw-mono">{r.perUnit.toFixed(2)}</td>
              <td className="aw-mono">{pdfBy[r.key] != null ? pdfBy[r.key].toFixed(2) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Annual({ rows, testid }) {
  const k = (v) => (v == null ? '—' : (v / 1e6).toFixed(2));
  return (
    <div className="abs-table-wrap">
      <table className="abs-table" data-testid={testid}>
        <thead>
          <tr><th>Year</th><th>Revenue</th><th>Hedge</th><th>Opex</th><th>Taxes</th><th>SNCF</th><th>Fees</th><th>Interest</th><th>Sched. amort</th><th>Var. amort / sweep</th><th>To parent</th><th>Notes EoY</th><th>Sweep mo</th></tr>
        </thead>
        <tbody>
          {(rows || []).slice(0, 15).map((r) => (
            <tr key={r.year}>
              <td>{r.year}</td><td>{k(r.revenue)}</td><td>{k(r.hedge)}</td><td>{k(-r.opex)}</td><td>{k(-r.taxes)}</td><td><b>{k(r.sncf)}</b></td>
              <td>{k(-r.seniorFees)}</td><td>{k(-r.interest)}</td><td>{k(-r.scheduledAmortization)}</td><td>{k(-r.variableAmortization)}</td><td><b>{k(r.toParent)}</b></td>
              <td>{k(r.endOutstanding)}</td><td>{r.sweepMonths || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="aw-small">$MM. First 15 calendar years shown.</div>
    </div>
  );
}

function Tracking({ t }) {
  if (!t) return null;
  return (
    <section className="abs-section" data-testid="abs-link-tracking">
      <h3>Production tracking — actual vs underwritten (2.6)</h3>
      <p className="aw-small">{t.method}. Threshold {fmtPct(t.threshold, 0)}.</p>
      <dl className="abs-dl">
        <div><dt>Pool tracking</dt><dd style={{ color: t.poolBreach ? '#dc2626' : undefined }}>{fmtPct(t.poolTrackingRatio)}</dd></div>
        <div><dt>Wells tracked / skipped</dt><dd>{t.wellsTracked} / {t.wellsSkipped}</dd></div>
        <div><dt>Wells below threshold</dt><dd>{t.wellsInBreach}</dd></div>
      </dl>
      <div className="abs-table-wrap">
        <table className="abs-table">
          <thead><tr><th>Well</th><th>As of</th><th>Actual ÷ UW</th></tr></thead>
          <tbody>
            {(t.wells || []).slice(0, 12).map((w) => (
              <tr key={w.assetId} className={w.breach ? 'abs-row-warn' : ''}><td>{w.name || w.assetId}</td><td>{w.asOf}</td><td>{fmtPct(w.trackingRatio)}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CapStruct() {
  const [res, setRes] = useState(null);
  const [absCoupon, setAbsCoupon] = useState(6.5);
  const [err, setErr] = useState(null);
  const load = useCallback((coupon) => {
    const overrides = coupon !== 6.5 ? { structures: [{ id: 'abs', coupon: coupon / 100 }] } : null;
    absApi.linkCapStruct({ overrides }).then(setRes).catch((e) => setErr(e.message));
  }, []);
  useEffect(() => { load(6.5); }, [load]);
  if (err) return <div className="aw-empty">Cap-structure compare failed: {err}</div>;
  if (!res) return null;
  const rs = res.results;
  const row = (label, f) => (
    <tr><td>{label}</td>{rs.map((r) => <td key={r.id} className="aw-mono">{f(r)}</td>)}</tr>
  );
  return (
    <section className="abs-section" data-testid="abs-link-capstruct">
      <h3>ABS vs RBL vs corporate notes (2.5) — {res.preset.label}</h3>
      <p className="aw-small">
        Preset from the PDF primer; every figure is editable input. ABS coupon{' '}
        <input type="number" step="0.25" value={absCoupon} onChange={(e) => setAbsCoupon(Number(e.target.value))} style={{ width: 70 }} data-testid="capstruct-abs-coupon" />%{' '}
        <button type="button" className="aw-btn" onClick={() => load(absCoupon)}>Recompute</button>
      </p>
      <div className="abs-table-wrap">
        <table className="abs-table">
          <thead><tr><th>$MM</th>{rs.map((r) => <th key={r.id}>{r.label}</th>)}</tr></thead>
          <tbody>
            {row('Advance rate', (r) => fmtPct(r.advanceRate, 0))}
            {row('Debt / equity', (r) => `${r.debtMM.toFixed(0)} / ${r.equityMM.toFixed(0)}`)}
            {row('Coupon', (r) => `${(r.coupon * 100).toFixed(2)}% ${r.rateType}`)}
            {row('5-yr distributions', (r) => r.distributionsMM.toFixed(0))}
            {row('Paydown', (r) => `${r.paydownMM.toFixed(0)} (${fmtPct(r.paydownPct, 0)})`)}
            {row('Debt at yr 5 / LTV', (r) => `${r.debtEndMM.toFixed(0)} / ${fmtPct(r.ltvEnd, 0)}`)}
            {row('Net equity value', (r) => r.netEquityMM.toFixed(0))}
            {row('Total return', (r) => r.totalReturnMM.toFixed(0))}
            {row('IRR', (r) => fmtPct(r.irr))}
            {row('MOIC', (r) => fmtX(r.moic))}
            {row('Risk', (r) => r.risk)}
          </tbody>
        </table>
      </div>
      {res.vsPdf && (
        <p className="aw-small" data-testid="capstruct-vs-pdf">
          Regression vs PDF table: max |Δ| distributions {Math.max(...res.vsPdf.filter((x) => x.metric === 'distributionsMM').map((x) => Math.abs(x.delta))).toFixed(1)}MM ·
          MOIC {Math.max(...res.vsPdf.filter((x) => x.metric === 'moic').map((x) => Math.abs(x.delta))).toFixed(3)} ·
          IRR {Math.max(...res.vsPdf.filter((x) => x.metric === 'irr').map((x) => Math.abs(x.delta * 100))).toFixed(1)}pt (PDF IRRs not reproducible from its own cash flows; ranking matches).
          Note: PDF bullet text swaps RBL/Corp IRR &amp; MOIC vs its table; the table is used.
        </p>
      )}
    </section>
  );
}

export default function AbsLink({ dealId }) {
  const [opts, setOpts] = useState(null);
  const [wf, setWf] = useState(null);
  const [pool, setPool] = useState(() => loadPool(typeof window !== 'undefined' ? window.sessionStorage : null, dealId));
  const [presetId, setPresetId] = useState('p90-hindcast');
  const [volumeMult, setVolumeMult] = useState(0.8);
  const [tranches, setTranches] = useState(null);
  const [triggers, setTriggers] = useState(null);
  const [aroZero, setAroZero] = useState(true);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setErr(null);
    Promise.all([absApi.linkOptions(dealId), fetchWorkflow(dealId)])
      .then(([o, w]) => {
        if (!alive) return;
        setOpts(o);
        setWf(w);
        setTranches(o.defaults.tranches);
        setTriggers(o.defaults.triggers);
        const d = downsideFromPreset(o.pCasePresets, 'p90-hindcast');
        setPresetId(d.presetId || 'stress80');
        setVolumeMult(d.volumeMult);
      })
      .catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [dealId]);

  useEffect(() => {
    savePool(typeof window !== 'undefined' ? window.sessionStorage : null, dealId, pool);
  }, [dealId, pool]);

  const run = useCallback(() => {
    setBusy(true);
    setErr(null);
    absApi
      .linkRun(linkRunBody({ dealId, pool, downside: { presetId, volumeMult }, tranches, triggers, aroTreatedAsZero: aroZero }))
      .then(setRes)
      .catch((e) => setErr(e.message))
      .finally(() => setBusy(false));
  }, [dealId, pool, presetId, volumeMult, tranches, triggers, aroZero]);

  const presets = useMemo(() => opts?.pCasePresets || [], [opts]);
  const st = res?.structure;
  const trancheIds = (tranches || []).map((t) => t.id);

  if (err && !opts) return <div className="error-screen" data-testid="abs-link-error"><h2>ABS link unavailable</h2><p>{err}</p></div>;
  if (!opts || !wf) return <div className="loading-screen"><div className="loading-spinner" /><p>Loading ABS link…</p></div>;

  const setTr = (i, k, v) => setTranches((ts) => ts.map((t, j) => (j === i ? { ...t, [k]: v } : t)));

  return (
    <div className="abs-link" data-testid="abs-link">
      <section className="abs-section">
        <h3>1 · Collateral pool (2.7)</h3>
        <p className="aw-small">
          Lasso wells on the map (or send a selection from Asset workflow). Empty selection = all approved wells.
          Approved forecasts: <b>{opts.approvedCount}</b>
          {opts.latestFreeze ? <> · latest freeze #{opts.latestFreeze.freezeId} <code>{opts.latestFreeze.contentSha256.slice(0, 12)}</code></> : ' · no freeze (live approvals used)'}
        </p>
        <WellMapV2 wells={wf.wells} selected={pool} onSelect={setPool} onFocus={() => {}} reviews={wf.reviews} />
        <div className="aw-small" data-testid="abs-link-pool">Pool: <b>{pool.length || 'all approved'}</b> wells</div>
      </section>

      <section className="abs-section">
        <h3>2 · Downside &amp; structure</h3>
        <div className="abs-link-controls">
          <label>Downside preset{' '}
            <select value={presetId || ''} data-testid="abs-link-preset" onChange={(e) => { const d = downsideFromPreset(presets, e.target.value); setPresetId(d.presetId); setVolumeMult(d.volumeMult); }}>
              {presets.map((p) => <option key={p.id} value={p.id}>{p.label} — {p.volumeMult}× ({p.source})</option>)}
            </select>
          </label>{' '}
          <label>Volume × <input type="number" step="0.01" min="0.05" max="2" value={volumeMult} onChange={(e) => setVolumeMult(Number(e.target.value))} style={{ width: 70 }} data-testid="abs-link-volmult" /></label>{' '}
          <label><input type="checkbox" checked={aroZero} onChange={(e) => setAroZero(e.target.checked)} /> ARO treated as zero (explicit override; unchecked = fail closed on unknown ARO) <span className="aro-basis" title="No dollar P&amp;A estimate exists in the Eagle VDR. Seller file setup: SALV NONE 0; IM p6 / Exec Summary p2: low P&amp;A exposure.">· basis: the seller forecast case carries no abandonment cost; IM cites low P&amp;A exposure (no $ estimate in VDR)</span></label>
        </div>
        <div className="abs-table-wrap">
          <table className="abs-table" data-testid="abs-link-tranches">
            <thead><tr><th>Tranche</th><th>Kind</th><th>Coupon %</th><th>Amort</th><th>Target DSCR</th><th>Tenor (yr)</th><th>VFN drawn $</th></tr></thead>
            <tbody>
              {(tranches || []).map((t, i) => (
                <tr key={t.id}>
                  <td>{t.id}</td><td>{t.kind}</td>
                  <td><input type="number" step="0.25" value={(t.coupon * 100).toFixed(2)} onChange={(e) => setTr(i, 'coupon', Number(e.target.value) / 100)} style={{ width: 70 }} /></td>
                  <td><select value={t.amort} onChange={(e) => setTr(i, 'amort', e.target.value)}><option>sculpted</option><option>level</option><option>bullet</option></select></td>
                  <td><input type="number" step="0.05" value={t.targetDscr ?? ''} onChange={(e) => setTr(i, 'targetDscr', Number(e.target.value))} style={{ width: 60 }} /></td>
                  <td><input type="number" step="1" value={t.tenorYears} onChange={(e) => setTr(i, 'tenorYears', Number(e.target.value))} style={{ width: 50 }} /></td>
                  <td>{t.kind === 'vfn' ? <input type="number" value={t.drawn || 0} onChange={(e) => setTr(i, 'drawn', Number(e.target.value))} style={{ width: 100 }} /> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className="aw-btn" data-testid="abs-link-add-vfn" disabled={(tranches || []).some((t) => t.kind === 'vfn')}
            onClick={() => setTranches((ts) => [...ts, { id: 'VFN', kind: 'vfn', coupon: 0.08, amort: 'bullet', tenorYears: 5, commitment: 2000000, drawn: 500000, undrawnFeePct: 0.005 }])}>
            + VFN
          </button>
        </div>
        <div className="abs-link-controls" data-testid="abs-link-triggers">
          {triggers && [['dscrMin', 'DSCR min (x)'], ['ltvMax', 'LTV max'], ['productionTrackingMin', 'Tracking min'], ['interestCoverageMin', 'Interest cover (x)'], ['legalFinalYears', 'Legal final (yr)']].map(([k, l]) => (
            <label key={k} style={{ marginRight: 10 }}>{l} <input type="number" step="0.01" value={triggers[k]} onChange={(e) => setTriggers({ ...triggers, [k]: Number(e.target.value) })} style={{ width: 64 }} /></label>
          ))}
        </div>
        <button type="button" className="abs-run-btn" onClick={run} disabled={busy} data-testid="abs-link-run">{busy ? 'Running…' : 'Run ABS link'}</button>
        {err && <div className="aw-empty" data-testid="abs-link-run-error">{err}</div>}
      </section>

      {res?.draftApprovals && (
        <div className="fin-draft-banner" data-testid="draft-banner" role="status">
          DRAFT · {res?.draftApprovals.count} forecasts carry a provisional approval — “{res?.draftApprovals.label}”. Not engineer-approved; never frozen.
        </div>
      )}
      {res && res.blocked && (
        <section className="abs-section" data-testid="abs-link-blocked">
          <h3>Underwriting blocked — no waterfall (fail closed)</h3>
          <p>Blockers: <code>{(res.blockers || []).join(', ')}</code></p>
        </section>
      )}

      {res && !res.blocked && st && (
        <>
          <section className="abs-section" data-testid="abs-link-sizing">
            <h3>3 · Tranche sizing (2.4)</h3>
            <dl className="abs-dl">
              {Object.entries(st.sizesMM).map(([k, v]) => <div key={k}><dt>Class {k}</dt><dd>{fmtMM(v)} <span className="aw-small">({st.solve.binding[k]})</span></dd></div>)}
              <div><dt>Total notes</dt><dd>{fmtMM(st.solve.totalMM)}</dd></div>
              <div><dt>Advance rate (notes ÷ SPV+hedge PV-10)</dt><dd>{fmtPct(st.solve.advanceRate)}</dd></div>
            </dl>
          </section>
          <section className="abs-section">
            <h3>4 · Securitized NCF waterfall (2.2)</h3>
            <PerUnit model={st.base.perUnitYear1} pdf={st.pdfPerUnitPreset} />
            <Annual rows={st.base.annual} testid="abs-link-annual" />
          </section>
          <section className="abs-section">
            <h3>5 · Triggers &amp; cash sweep (2.3)</h3>
            <div className="abs-link-grid">
              <SummaryKpis s={st.base.summary} label="Base (approved forecasts)" testid="abs-link-base-kpis" />
              {st.downside && <SummaryKpis s={st.downside.summary} label={`Downside ${res.runs.downside?.presetId || ''} (${res.runs.downside?.volumeMult}× volume)`} testid="abs-link-down-kpis" />}
            </div>
            <h4>Base</h4>
            <TriggerTimeline rows={st.base.monthly} testid="abs-link-timeline-base" />
            {st.downside && (<><h4>Downside</h4><TriggerTimeline rows={st.downside.monthly} testid="abs-link-timeline-down" /></>)}
            <h4>Note balances ($MM) — base vs downside</h4>
            <MiniLines series={[...balanceSeries(st.base.monthly, trancheIds).map((s) => ({ ...s, id: `${s.id} base` })), ...(st.downside ? balanceSeries(st.downside.monthly, trancheIds).map((s) => ({ ...s, id: `${s.id} down` })) : [])]} testid="abs-link-balances" />
          </section>
          <Tracking t={res.tracking} />
          <section className="abs-section" data-testid="abs-link-provenance">
            <h3>Provenance</h3>
            <dl className="abs-dl">
              <div><dt>Package source</dt><dd>{res.packageSource} · reviews: {res.reviewSource}</dd></div>
              <div><dt>Base runHash</dt><dd><code>{res.runs.base.runHash}</code> {res.runs.base.durable ? '(durable)' : '(not durable)'}</dd></div>
              <div><dt>Downside runHash</dt><dd><code>{res.runs.downside?.runHash}</code></dd></div>
              <div><dt>Approved case</dt><dd><code>{res.approvedCaseId}</code> · {JSON.stringify(res.provenance.basisCounts)}</dd></div>
              <div><dt>Freeze</dt><dd>{res.freeze ? `#${res.freeze.freezeId} ${res.freeze.contentSha256.slice(0, 16)}` : 'none'}</dd></div>
              <div><dt>Excluded wells</dt><dd>{(res.provenance.excluded || []).length}</dd></div>
            </dl>
          </section>
        </>
      )}
      <CapStruct />
    </div>
  );
}
